import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Phase 4.4: role-driven menu visibility. null => not loaded / show-all (legacy)
  const [effectiveMenus, setEffectiveMenus] = useState(null);

  const loadEffectiveMenus = async () => {
    try {
      const res = await authAPI.getEffectiveMenus();
      setEffectiveMenus(res.data?.all ? null : (res.data?.menus || []));
    } catch {
      setEffectiveMenus(null);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      authAPI.getMe()
        .then(response => {
          setUser(response.data);
          localStorage.setItem('user', JSON.stringify(response.data));
          return loadEffectiveMenus();
        })
        .catch(() => {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password, latitude = null, longitude = null) => {
    const loginData = { email, password };
    if (latitude !== null && longitude !== null) {
      loginData.latitude = latitude;
      loginData.longitude = longitude;
    }
    const response = await authAPI.login(loginData);
    const { access_token, user: userData, must_change_password } = response.data;
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    await loadEffectiveMenus();
    return { user: userData, must_change_password };
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setEffectiveMenus(null);
  };

  // === Access Model V2: bridge legacy menuKey → new slug ===
  const MENU_KEY_TO_SLUG = {
    fault_log: 'fault-log',
    site_diary: 'site-diary',
    team_members: 'team-members',
    time_attendance: 'time-attendance',
    performance_reviews: 'performance-reviews',
    system_settings: 'system-settings',
    user_management: 'user-management',
    popia: 'popia-compliance',
    incidents: 'incident-reporting',
    calendar: 'schedule',
    invoicing: 'finance-invoicing',
    claims: 'finance-claims',
    purchase_orders: 'finance-purchase-orders',
    payment_certificates: 'finance-payment-certificates',
    payroll: 'finance-payroll',
  };
  const toSlug = (menuKey) => MENU_KEY_TO_SLUG[menuKey] || menuKey.replace(/_/g, '-');

  // Check if user can access a specific menu item
  const canAccessMenu = (menuKey) => {
    if (!user) return false;
    const slug = toSlug(menuKey);

    // Access Model V2 short-circuit for super admins
    if (user.is_super_admin) return true;

    // V2 authoritative: if the user has permissions.allowed_menus explicitly set, use it.
    // We treat a *present* array as authoritative even if empty — that's the "no access"
    // default. Only fall back to legacy when the field is missing entirely (pre-migration users).
    const v2 = user.permissions && user.permissions.allowed_menus;
    if (Array.isArray(v2)) {
      return v2.includes(slug);
    }

    // --- Legacy fallback (pre-V2 users, no permissions object) ---
    // Admin always has full access
    if (user.role === 'admin') return true;

    // Phase 4.4: if the user's system role defines a menu set, it is authoritative
    if (Array.isArray(effectiveMenus)) {
      return effectiveMenus.includes(menuKey);
    }
    
    // Clock-in only users can ONLY access time_attendance (and dashboard for navigation)
    if (user.is_clock_in_only) {
      // Check if time_attendance is explicitly enabled in menu_permissions
      if (user.menu_permissions && user.menu_permissions.time_attendance === true) {
        return menuKey === 'time_attendance' || menuKey === 'dashboard';
      }
      // Default: clock-in only users get time_attendance and dashboard
      return menuKey === 'time_attendance' || menuKey === 'dashboard';
    }
    
    // IMPORTANT: If user has ANY custom menu permissions set, use ONLY those permissions
    // This allows menu permissions to OVERRIDE the role-based defaults completely
    if (user.menu_permissions && Object.keys(user.menu_permissions).length > 0) {
      // Check if at least one permission is explicitly set to true
      const hasExplicitPermissions = Object.values(user.menu_permissions).some(v => v === true);
      if (hasExplicitPermissions) {
        // Only allow access if explicitly set to true in menu_permissions
        return user.menu_permissions[menuKey] === true;
      }
    }
    
    // Default permissions based on role (only used if no custom permissions are set)
    const defaultPermissions = {
      dashboard: ['admin', 'manager', 'technician', 'site_worker'],
      jobs: ['admin', 'manager', 'technician'],
      leads: ['admin', 'manager'],
      fault_log: ['admin', 'manager'],
      boq: ['admin', 'manager'],
      requisitions: ['admin', 'manager'],
      time_attendance: ['admin', 'manager', 'technician', 'site_worker'],
      teams: ['admin', 'manager'],
      team_members: ['admin', 'manager'],
      assets: ['admin', 'manager'],
      cashflow: ['admin', 'manager'],
      reports: ['admin', 'manager'],
      incidents: ['admin', 'manager'],
      popia: ['admin'],
      user_management: ['admin'],
      system_settings: ['admin'],
    };
    
    return defaultPermissions[menuKey]?.includes(user.role) || false;
  };

  // Check if user can view job costs
  const canViewJobCosts = () => {
    if (!user) return false;
    return user.can_view_job_costs !== false; // Default to true if not set
  };

  // ===== Access Model V2 =====
  const hasMenuAccess = (slug) => {
    if (!user) return false;
    if (user.is_super_admin) return true;
    const allowed = user.permissions?.allowed_menus || [];
    return allowed.includes(slug);
  };

  const hasProjectPermission = (projectId, action) => {
    if (!user) return false;
    if (user.is_super_admin) return true;
    const row = (user.permissions?.projects || []).find(p => p.project_id === projectId);
    return row ? !!row[action] : false;
  };

  const isSuperAdmin = () => !!user?.is_super_admin;

  // Force a fresh fetch of the current user (used after admin updates own permissions).
  const refreshUser = async () => {
    try {
      const res = await authAPI.getMe();
      setUser(res.data);
      localStorage.setItem('user', JSON.stringify(res.data));
      await loadEffectiveMenus();
      return res.data;
    } catch (e) {
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      logout, 
      canAccessMenu,
      canViewJobCosts,
      hasMenuAccess,
      hasProjectPermission,
      isSuperAdmin,
      refreshUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};